import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { downloadBlob } from "tools/browser/download"
import { zipSync } from "fflate"
import {
    BatchEditMissionDetailsBody,
    CreateManualMissionBody,
    FlightPathPoint,
    MissionTablePayload,
    UpdatableMission,
} from "@nova/schemas"
import { invalidateMissionCustomFields } from "../missions/linked-layers/MissionCustomFieldsQueries"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { invalidatePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useAppSelector } from "integrations/redux/hooks"
import { useCallback, useMemo } from "react"
import useSWR, { mutate as globalMutate } from "swr"
import useSWRMutation from "swr/mutation"
import { useSWRNoRevalidate } from "@nova/swr"

const getMission = (orgId: string, missionId: string) =>
    fetchApiGateway("get /org/:orgId/fleet/mission/:missionId", {
        params: { orgId, missionId },
    })

export const useMission = ({ missionId }: { missionId: string }) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWRNoRevalidate(["get-mission", orgId, missionId], () =>
        orgId ? getMission(orgId, missionId) : undefined
    )

    return { mission: data ?? null, isLoading }
}

const invalidateMissionTableCache = () =>
    Promise.all([
        globalMutate((key) => Array.isArray(key) && key[0] === "mission-table"),
        invalidatePilotTrackingQuery("missions"),
        invalidatePilotTrackingQuery("metadata"),
    ])

const fetchMissionTable = async (orgId: string, body: MissionTablePayload) => {
    const res = await fetchApiGateway("post /org/:orgId/fleet/mission/table", {
        method: "POST",
        body: body,
        params: { orgId },
    })
    return res
}

export function useMissionTableQuery(body: MissionTablePayload = {}) {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const key = orgId ? ["mission-table", orgId, body] : null

    const { data, error, isLoading, mutate } = useSWR(
        key,
        () => (orgId ? fetchMissionTable(orgId, body) : undefined),
        {
            keepPreviousData: true,
            revalidateOnFocus: false,
        }
    )

    return {
        items: data?.items ?? [],
        total: data?.total ?? 0,
        isLoading,
        error,
        mutate: () => mutate(data),
    }
}

const createMissionGroup = async (orgId: string, missionIds: string[]) => {
    return fetchApiGateway("post /org/:orgId/fleet/mission/group", {
        method: "POST",
        body: { missionIds },
        params: { orgId },
    })
}

export function useCreateMissionGroup() {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating, error, data } = useSWRMutation(
        "create-mission-group",
        (key, req: { arg: string[] }) => (orgId ? createMissionGroup(orgId, req.arg) : undefined),
        { onSuccess: invalidateMissionTableCache }
    )

    return {
        createGroup: trigger,
        createdGroup: data,
        isCreating: isMutating,
        error,
    }
}

const createManualMission = (orgId: string, body: CreateManualMissionBody) =>
    fetchApiGateway("post /org/:orgId/fleet/mission", { params: { orgId }, body })

export const useCreateManualMission = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "create-manual-mission",
        (_key, { arg }: { arg: CreateManualMissionBody }) =>
            orgId ? createManualMission(orgId, arg) : undefined,
        { onSuccess: invalidateMissionTableCache }
    )

    return { createMission: trigger, isCreating: isMutating }
}

const updateMission = (orgId: string, missionId: string, body: UpdatableMission) => {
    return fetchApiGateway("patch /org/:orgId/fleet/mission/:missionId", {
        body,
        params: { orgId, missionId },
    })
}

export const useUpdateMission = (missionId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "update-mission",
        (_key, { arg }: { arg: UpdatableMission }) =>
            orgId ? updateMission(orgId, missionId, arg) : undefined,
        { onSuccess: invalidateMissionTableCache }
    )

    return {
        updateMission: trigger,
        isUpdating: isMutating,
    }
}

export const useBulkAssignDrone = (missionIds: string[]) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const assignDrone = useCallback(
        async (aircraftId: string) => {
            if (!orgId) return
            await fetchApiGateway("patch /org/:orgId/fleet/mission", {
                params: { orgId },
                body: { ids: missionIds, aircraftId },
            })
            await invalidateMissionTableCache()
        },
        [missionIds, orgId]
    )

    return { assignDrone }
}

export const useBulkAssignPilot = (missionIds: string[]) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const assignPilot = useCallback(
        async (userId: string) => {
            if (!orgId) return
            await fetchApiGateway("patch /org/:orgId/fleet/mission", {
                params: { orgId },
                body: { ids: missionIds, userId },
            })
            await invalidateMissionTableCache()
        },
        [missionIds, orgId]
    )

    return { assignPilot }
}

export const useBulkEditMissionDetails = (missionIds: string[]) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const editDetails = useCallback(
        async (details: Omit<BatchEditMissionDetailsBody, "ids">) => {
            if (!orgId) return
            await fetchApiGateway("patch /org/:orgId/fleet/mission", {
                params: { orgId },
                body: { ids: missionIds, ...details },
            })
            await Promise.all([invalidateMissionTableCache(), invalidateMissionCustomFields()])
        },
        [missionIds, orgId]
    )

    return { editDetails }
}

const deleteMission = (orgId: string, missionId: string) =>
    fetchApiGateway("delete /org/:orgId/fleet/mission/:missionId", {
        params: { orgId, missionId },
    })

export const useBulkDeleteMissions = (missionIds: string[]) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const bulkDeleteMissions = useCallback(async () => {
        if (!orgId) return
        await fetchApiGateway("delete /org/:orgId/fleet/mission", {
            params: { orgId },
            body: { ids: missionIds },
        })
        await invalidateMissionTableCache()
    }, [missionIds, orgId])

    return { bulkDeleteMissions }
}

export const useDeleteGroup = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const deleteGroup = useCallback(
        async (groupId: string) => {
            if (!orgId) return
            await fetchApiGateway("delete /org/:orgId/fleet/mission/group/:groupId", {
                params: { orgId, groupId },
            })
            await invalidateMissionTableCache()
        },
        [orgId]
    )

    return { deleteGroup }
}

const ungroupMissionsRaw = (orgId: string, groupId: string) =>
    fetchApiGateway("post /org/:orgId/fleet/mission/group/:groupId/ungroup", {
        params: { orgId, groupId },
    })

export const useUngroupMissions = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "ungroup-missions",
        (_key, { arg: groupId }: { arg: string }) =>
            orgId ? ungroupMissionsRaw(orgId, groupId) : undefined,
        { onSuccess: invalidateMissionTableCache }
    )

    return { ungroupMissions: trigger, isUngrouping: isMutating }
}

export const useRenameGroup = (groupId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const renameGroup = useCallback(
        async (name: string) => {
            if (!orgId) return
            await fetchApiGateway("patch /org/:orgId/fleet/mission/group/:groupId", {
                body: { name },
                params: { orgId, groupId },
            })
            await invalidateMissionTableCache()
        },
        [orgId, groupId]
    )

    return { renameGroup }
}

const addMissionToGroupRaw = (orgId: string, groupId: string, missionId: string) =>
    fetchApiGateway("put /org/:orgId/fleet/mission/group/:groupId/missions/:missionId", {
        params: { orgId, groupId, missionId },
    })

const removeMissionFromGroupRaw = (orgId: string, groupId: string, missionId: string) =>
    fetchApiGateway("delete /org/:orgId/fleet/mission/group/:groupId/missions/:missionId", {
        params: { orgId, groupId, missionId },
    })

export const useMoveMissionInGroups = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const moveMission = useCallback(
        async (args: {
            missionId: string
            fromGroupId: string | null
            toGroupId: string | null
        }) => {
            if (!orgId) return
            if (args.fromGroupId) {
                await removeMissionFromGroupRaw(orgId, args.fromGroupId, args.missionId)
            }
            if (args.toGroupId) {
                await addMissionToGroupRaw(orgId, args.toGroupId, args.missionId)
            }
            await invalidateMissionTableCache()
        },
        [orgId]
    )

    return { moveMission }
}

export const useDownloadFlightRecords = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "download-flight-records",
        async (_key, { arg: missionIds }: { arg: string[] }) => {
            if (!orgId) return
            const results = await fetchApiGateway(
                "post /org/:orgId/fleet/missions/export/flight-records",
                { params: { orgId }, body: { missionIds } }
            )
            if (results.length === 1) {
                const { fileName, url } = results[0]
                await downloadBlob(fileName, await fetch(url).then((r) => r.blob()))
                return
            }
            const files: Record<string, [Uint8Array, { level: 0 }]> = {}
            await Promise.all(
                results.map(async ({ fileName, url }) => {
                    const response = await fetch(url)
                    const buffer = await response.arrayBuffer()
                    files[fileName] = [new Uint8Array(buffer), { level: 0 }]
                })
            )
            const zip = zipSync(files)
            await downloadBlob("flight-records.zip", new Blob([zip], { type: "application/zip" }))
        }
    )

    return { downloadFlightRecords: trigger, isDownloading: isMutating }
}

export const useExportFlightPaths = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "export-flight-paths",
        async (_key, { arg: missionIds }: { arg: string[] }) => {
            if (!orgId) return
            const response = await fetchApiGateway(
                "post /org/:orgId/fleet/missions/export/flight-paths",
                { rawResponse: true, params: { orgId }, body: { missionIds } }
            )
            const blob = await response.blob()
            const isSingle = missionIds.length === 1
            await downloadBlob(isSingle ? `${missionIds[0]}.kml` : "flight-paths.zip", blob)
        }
    )

    return { exportFlightPaths: trigger, isExporting: isMutating }
}

const fetchMissionFlightPaths = (orgId: string, missionIds: string[]) =>
    fetchApiGateway("post /org/:orgId/fleet/missions/flight-paths", {
        method: "POST",
        body: { missionIds },
        params: { orgId },
    })

export function useMissionFlightPath(missionId: string | null): {
    points: FlightPathPoint[] | null
} {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const key = orgId && missionId ? ["mission-flight-path", orgId, missionId] : null

    const { data } = useSWRNoRevalidate(key, () =>
        orgId && missionId ? fetchMissionFlightPaths(orgId, [missionId]) : undefined
    )

    return useMemo(() => {
        const fp = data?.find((p) => p.missionId === missionId)
        return { points: fp?.points ?? null }
    }, [data, missionId])
}

export const useDeleteMission = (missionId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "delete-mission",
        () => (orgId ? deleteMission(orgId, missionId) : undefined),
        { onSuccess: invalidateMissionTableCache }
    )

    return {
        deleteMission: trigger,
        isDeleting: isMutating,
    }
}
